import { supabase } from './supabaseClient';

/**
 * 무료체험 유효성 확인 함수
 * @param {Object} trial - 무료체험 정보
 * @param {Object} subs - 구독 정보
 * @returns {boolean} 무료체험이 유효한지 여부
 */
export const checkFreeTrialValidity = (trial, subs) => {
    if (!trial) return false;
    
    if (trial.status !== 'trialing' && trial.plan_name !== 'FREE_TRIAL') {
        return false;
    }
    
    if (subs) {
        return true;
    }
    
    const now = new Date();
    const endDate = new Date(trial.trial_end);
    return now <= endDate;
};

/**
 * 구독 상태 확인 함수
 * @param {Object} session - 사용자 세션 정보
 * @returns {Object} { loading, hasAccess, subscription, freeTrialInfo }
 */
export const checkSubscriptionStatus = async (session) => {
    const result = {
        loading: true,
        hasAccess: false,
        subscription: null,
        freeTrialInfo: null
    };

    if (!session?.user) {
        return { ...result, loading: false };
    }

    try {
        // 1) 유료 구독 정보 확인 (FREE_TRIAL이 아닌 것만)
        const { data: subs, error: subsError } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', session.user.id)
            .eq('status', 'active')
            .not('plan_name', 'eq', 'FREE_TRIAL')
            .single();

        if (subsError && subsError.code !== 'PGRST116') {
            console.error('Subscription fetch error:', subsError);
        } else {
            result.subscription = subs;
        }

        // 2) 무료체험 정보 확인 (새로운 방식: subscriptions 테이블에서)
        const { data: trialData, error: trialError } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', session.user.id)
            .eq('plan_name', 'FREE_TRIAL')
            .eq('status', 'trialing')
            .single();

        if (trialError && trialError.code !== 'PGRST116') {
            console.error('Free trial fetch error:', trialError);
        } else {
            result.freeTrialInfo = trialData;
        }

        // 접근 권한 결정
        const hasValidSubscription = subs && subs.status === 'active';
        const hasValidTrial = checkFreeTrialValidity(trialData, subs);
        result.hasAccess = hasValidSubscription || hasValidTrial;
    } catch (error) {
        console.error('Failed to check subscription status:', error);
    } finally {
        result.loading = false;
    }

    return result;
};

/**
 * 구독 상태를 관리하는 커스텀 훅
 * 이 훅은 컴포넌트에서 사용할 수 있도록 React Hook 패턴으로 제공됩니다.
 */
export const useSubscriptionStatus = () => {
    // 이 함수는 필요시 React Hook으로 확장 가능합니다.
    // 현재는 단순히 checkSubscriptionStatus를 export합니다.
    return { checkSubscriptionStatus, checkFreeTrialValidity };
};